
import { CreditCardBill } from '@/types/bill';
import { FutureRelease, InstallmentGroup } from '@/types';
import { 
  saveBillToSupabase, 
  loadBillsFromSupabase, 
  isUserAuthenticated as isBillsUserAuthenticated 
} from './supabase-bills-operations';
import { 
  saveFutureReleaseToSupabase, 
  loadFutureReleasesFromSupabase,
  saveInstallmentGroupToSupabase,
  loadInstallmentGroupsFromSupabase,
  isUserAuthenticated as isFutureUserAuthenticated 
} from './supabase-future-releases-operations';

const MIGRATION_FLAGS = {
  BILLS_MIGRATED: 'bills_migrated_to_supabase',
  FUTURE_RELEASES_MIGRATED: 'future_releases_migrated_to_supabase'
};

export const migrateBillsToSupabase = async (): Promise<void> => {
  if (!await isBillsUserAuthenticated()) {
    console.log('[Migration] User not authenticated, skipping bills migration');
    return;
  }

  const migrationFlag = localStorage.getItem(MIGRATION_FLAGS.BILLS_MIGRATED);
  if (migrationFlag === 'true') {
    console.log('[Migration] Bills already migrated, skipping');
    return;
  }

  console.log('[Migration] Starting bills migration to Supabase...');

  try {
    // Backup existing localStorage data
    const localBillsData = localStorage.getItem('creditCardBills');
    if (localBillsData) {
      localStorage.setItem('creditCardBills_backup', localBillsData);
      
      const localBills: CreditCardBill[] = JSON.parse(localBillsData);
      console.log(`[Migration] Found ${localBills.length} bills in localStorage`);

      // Check if there's already data in Supabase
      const existingBills = await loadBillsFromSupabase();
      if (existingBills.length === 0 && localBills.length > 0) {
        // Migrate each bill to Supabase
        for (const bill of localBills) {
          await saveBillToSupabase(bill);
        }
        console.log(`[Migration] Successfully migrated ${localBills.length} bills to Supabase`);
      } else {
        console.log(`[Migration] Supabase already has ${existingBills.length} bills, skipping migration`);
      }
    }

    // Mark migration as completed
    localStorage.setItem(MIGRATION_FLAGS.BILLS_MIGRATED, 'true');
    console.log('[Migration] Bills migration completed');
  } catch (error) {
    console.error('[Migration] Error migrating bills:', error);
    // Don't mark as migrated on error
  }
};

export const migrateFutureReleasesToSupabase = async (): Promise<void> => {
  if (!await isFutureUserAuthenticated()) {
    console.log('[Migration] User not authenticated, skipping future releases migration');
    return;
  }

  const migrationFlag = localStorage.getItem(MIGRATION_FLAGS.FUTURE_RELEASES_MIGRATED);
  if (migrationFlag === 'true') {
    console.log('[Migration] Future releases already migrated, skipping');
    return;
  }

  console.log('[Migration] Starting future releases migration to Supabase...');

  try {
    // Backup existing localStorage data
    const localReleasesData = localStorage.getItem('futureReleases');
    const localGroupsData = localStorage.getItem('installmentGroups');
    
    if (localReleasesData) {
      localStorage.setItem('futureReleases_backup', localReleasesData);
    }
    if (localGroupsData) {
      localStorage.setItem('installmentGroups_backup', localGroupsData);
    }

    // Migrate future releases
    if (localReleasesData) {
      const localReleases: FutureRelease[] = JSON.parse(localReleasesData);
      console.log(`[Migration] Found ${localReleases.length} future releases in localStorage`);

      const existingReleases = await loadFutureReleasesFromSupabase();
      if (existingReleases.length === 0 && localReleases.length > 0) {
        for (const release of localReleases) {
          await saveFutureReleaseToSupabase(release);
        }
        console.log(`[Migration] Successfully migrated ${localReleases.length} future releases to Supabase`);
      }
    }

    // Migrate installment groups
    if (localGroupsData) {
      const localGroups: InstallmentGroup[] = JSON.parse(localGroupsData);
      console.log(`[Migration] Found ${localGroups.length} installment groups in localStorage`);

      const existingGroups = await loadInstallmentGroupsFromSupabase();
      if (existingGroups.length === 0 && localGroups.length > 0) {
        for (const group of localGroups) {
          await saveInstallmentGroupToSupabase(group);
        }
        console.log(`[Migration] Successfully migrated ${localGroups.length} installment groups to Supabase`);
      }
    }

    // Mark migration as completed
    localStorage.setItem(MIGRATION_FLAGS.FUTURE_RELEASES_MIGRATED, 'true');
    console.log('[Migration] Future releases migration completed');
  } catch (error) {
    console.error('[Migration] Error migrating future releases:', error);
    // Don't mark as migrated on error
  }
};

export const performDataMigration = async (): Promise<void> => {
  console.log('[Migration] Starting data migration process...');
  await migrateBillsToSupabase();
  await migrateFutureReleasesToSupabase();
  console.log('[Migration] Data migration process completed');
};
